import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { LeftMenu } from '~/editor/leftMenu/LeftMenu';
import { MainContent } from '~/editor/mainContent/MainContent';
import { useEditorStore, EditorSequence, EditorChapter } from '~/shell/Store';
import { CloseButton } from '~/editor/leftMenu/CloseButton';
import { Spinner } from '~/ui/spinner/Spinner';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { useLocation } from 'react-router-dom';
import { CourseCreated } from '~/editor/ui/CourseCreated/CourseCreated';
import Tooltips from '~/editor/tooltips/Tooltips';
import posthog from 'posthog-js';
import { editorAnalyticsConfig } from './posthogPrivacy';
import { GlobalRewritePortal } from '~/training/sequence/templates/items/text/GlobalRewritePortal';
import { setupBeforeUnloadHandler } from '~/editor/lib/beforeUnloadHandler';
import { GenerationFeedback } from '~/editor/generationFeedback/GenerationFeedback';
import { isTranslationsOnlySession } from '~/editor/translation/translationOnlySession';

const Wrapper = styled.div`
  color: #444746;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

const LoadError = styled.div`
  padding: 32px;
`;

interface SequenceData {
  sequence: EditorSequence;
  sequenceMetadata?: {
    canUndo: boolean;
    canRedo: boolean;
  };
}

const generateExitFallbackPath = (
  companyId: string | null,
  trainingId: string | null
) => {
  const resultPath = [
    companyId ? `companies/${companyId}` : null,
    companyId && !trainingId ? 'course_authoring/trainings' : null,
    trainingId ? `trainings/${trainingId}` : null
  ]
    .filter(Boolean)
    .join('/');

  return `/${resultPath}`;
};

export const Editor = () => {
  const {
    sequence,
    training,
    setTraining,
    setSequence,
    apiService,
    collectIds,
    setApiService,
    setShowTooltips,
    setFeatureFlags,
    hasFeatureFlag,
    setUndoRedoState,
    fetchGlobalPlaceholders,
    fetchTemplates,
    refreshTrainingOutline,
    setTranslationScope
  } = useEditorStore();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [translationsOnly] = useState(() =>
    isTranslationsOnlySession(location.search)
  );
  const [showMenu, setShowMenu] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<'editor' | 'preview' | 'translation'>(
    translationsOnly ? 'translation' : 'editor'
  );
  const [showTooltipSection, setShowTooltipSection] = useState(false);

  const token = queryParams.get('token');
  const company_id = queryParams.get('company_id');
  const training_id = queryParams.get('training_id');
  const sequence_id = queryParams.get('sequence_id');

  const exitRedirectUrl =
    queryParams.get('exitRedirectUrl') ||
    queryParams.get('trainingCatalog') ||
    generateExitFallbackPath(company_id, training_id);
  const newTraining = queryParams.get('new_training');
  const product_tour = queryParams.get('product_tour');
  const publishButtonTitle = queryParams.get('publishButtonTitle');
  const publishRedirectUrl = queryParams.get('publishRedirectUrl');
  const requestedEditingMode = queryParams.get('editingMode');
  const editingMode =
    requestedEditingMode === 'private' || requestedEditingMode === 'live'
      ? requestedEditingMode
      : null;
  const translationsLockedUntilPublish =
    queryParams.get('translationsLockedUntilPublish') === 'true';
  const closeIconOnRight = queryParams.get('closeIconOnRight') === 'true';

  useEffect(() => {
    const shouldShowWalkthrough = product_tour == 'true' || editingMode != null;

    if (shouldShowWalkthrough && sequence && !showTooltipSection) {
      setShowTooltips(true);
      setShowTooltipSection(true);
    }
  }, [editingMode, product_tour, sequence, showTooltipSection]);

  const [showCourseCreated, setShowCourseCreated] = useState(
    newTraining == 'true'
  );

  const getTrainingAndSequences = () => {
    if (!apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    if (!training_id) {
      return;
    }

    apiService
      .getCourseInfo(training_id)
      .then(data => {
        if (translationsOnly) {
          setShowLoader(false);
          if (data?.training) {
            setTraining(data.training);
            setLoadError(null);
            const firstChapterSequence = data.training.chapters
              ?.flatMap(
                (chapter: EditorChapter) => chapter.chapterSequences ?? []
              )
              .find(
                (chapterSequence: EditorChapter['chapterSequences'][number]) =>
                  !sequence_id ||
                  String(chapterSequence.sequence.id) === sequence_id
              );
            if (firstChapterSequence) {
              setTranslationScope({
                kind: 'sequence',
                sequenceId: firstChapterSequence.sequence.id
              });
            }
          } else {
            setLoadError('The course could not be loaded. Please try again.');
          }
          return;
        }

        const firstSequence = data.training.chapters.reduce(
          (
            found: EditorChapter['chapterSequences'][number] | null,
            chapter: EditorChapter
          ) => {
            if (found) return found;
            if (sequence_id) {
              return (
                chapter.chapterSequences?.find(
                  cs => String(cs.sequence.id) === sequence_id
                ) || null
              );
            }
            return chapter.chapterSequences?.[0] || null;
          },
          null
        );

        const sequencePromises = firstSequence
          ? [apiService.getSequenceInfo(firstSequence.sequence.id)]
          : [];

        Promise.all<SequenceData>(sequencePromises).then(sequenceDataArray => {
          setShowLoader(false);
          const loadedSequence = sequenceDataArray[0]?.sequence;
          if (loadedSequence) {
            setSequence(loadedSequence);
            setLoadError(null);
          } else if (firstSequence) {
            setLoadError(
              'This slide could not be loaded. Select another slide from the menu.'
            );
          }

          if (
            sequenceDataArray[0]?.sequenceMetadata &&
            hasFeatureFlag('course_editor_backend_undo_redo')
          ) {
            setUndoRedoState(
              sequenceDataArray[0].sequenceMetadata.canUndo,
              sequenceDataArray[0].sequenceMetadata.canRedo
            );
          }

          const updatedTraining = {
            ...data.training,
            chapters: data.training.chapters.map((chapter: EditorChapter) => ({
              ...chapter,
              chapterSequences: chapter?.chapterSequences?.map(
                (
                  chapterSequence: EditorChapter['chapterSequences'][number]
                ) => {
                  const sequenceData = sequenceDataArray.find(
                    (seqData: SequenceData) =>
                      seqData?.sequence?.id === chapterSequence.sequence.id
                  );

                  if (sequenceData) {
                    return {
                      ...chapterSequence,
                      sequence: {
                        ...chapterSequence.sequence,
                        ...sequenceData.sequence
                      }
                    };
                  }
                  return chapterSequence;
                }
              )
            }))
          };

          setTraining(updatedTraining);
        });
      })
      .catch(error => {
        setShowLoader(false);
        setLoadError('The course could not be loaded. Please try again.');
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: error
        });
      });
  };

  useEffect(() => {
    if (token && company_id) {
      setApiService(token, company_id, training_id);
    }
  }, [token, company_id, training_id]);

  useEffect(() => {
    if (apiService) {
      // Only this connection's answer counts: one still on its way for the
      // company or course before must not set the flags, nor load its course.
      const isCurrent = () =>
        useEditorStore.getState().apiService === apiService;
      apiService
        .getCompanyWithFeatureFlags()
        .then(data => {
          if (!isCurrent()) return;
          // Only a list counts: anything else (a string, an object) is none.
          if (
            data &&
            data.company &&
            Array.isArray(data.company.featureFlags)
          ) {
            setFeatureFlags(data.company.featureFlags);
          } else {
            setFeatureFlags([]);
          }
          getTrainingAndSequences();
          if (!translationsOnly) {
            fetchGlobalPlaceholders();
            fetchTemplates();
          }
        })
        .catch(error => {
          if (!isCurrent()) return;
          console.error('Failed to fetch feature flags:', error);
          setFeatureFlags([]);
          getTrainingAndSequences();
          if (!translationsOnly) {
            fetchGlobalPlaceholders();
            fetchTemplates();
          }
        });
    }
  }, [apiService]);

  const setEditorMode = useCallback(
    (newMode: 'editor' | 'preview' | 'translation') => {
      setMode(translationsOnly ? 'translation' : newMode);
    },
    [translationsOnly]
  );

  useEffect(() => {
    setShowMenu(mode === 'editor' || mode === 'translation');

    if (mode === 'preview' && apiService) {
      getTrainingAndSequences();
    }
  }, [mode, apiService]);

  useEffect(() => {
    if (
      window &&
      window.location &&
      (window?.location?.hostname === 'localhost' ||
        window?.location?.hostname === 'app.easyllama.local')
    ) {
      return;
    }

    if (
      typeof window !== 'undefined' &&
      !(posthog as { __loaded: boolean }).__loaded
    ) {
      posthog.init(
        'phc_qvfwkRLvz4JIch2ShspuB7F7bmDZjO9t9tE08yNc0dq',
        editorAnalyticsConfig
      );
    }
  }, []);

  useEffect(() => {
    const refreshOutline = () => {
      if (document.visibilityState === 'visible') void refreshTrainingOutline();
    };
    window.addEventListener('focus', refreshOutline);
    document.addEventListener('visibilitychange', refreshOutline);
    return () => {
      window.removeEventListener('focus', refreshOutline);
      document.removeEventListener('visibilitychange', refreshOutline);
    };
  }, [refreshTrainingOutline]);

  useEffect(() => {
    const cleanup = setupBeforeUnloadHandler();
    return cleanup;
  }, []);

  return (
    <Wrapper>
      {!showCourseCreated && mode === 'editor' && (
        <Tooltips
          editingMode={editingMode}
          productTour={product_tour == 'true'}
        />
      )}
      {showCourseCreated && (
        <CourseCreated onClose={() => setShowCourseCreated(false)} />
      )}
      {!translationsOnly && <GlobalRewritePortal />}
      {showLoader ? (
        <Spinner />
      ) : (
        <Content>
          {showMenu && !sequence_id && (
            <LeftMenu
              mode={mode === 'translation' ? 'translation' : 'editor'}
              translationsOnly={translationsOnly}
            />
          )}
          {(mode === 'editor' || mode === 'translation') &&
            !sequence_id &&
            !showCourseCreated && (
              <CloseButton showMenu={showMenu} setShowMenu={setShowMenu} />
            )}
          {((translationsOnly && training?.id) ||
            (sequence && sequence.id)) && (
            <MainContent
              key={
                translationsOnly
                  ? `translations-${training?.id}`
                  : collectIds(sequence!) + '-' + sequence?.items?.length
              }
              {...(sequence || {})}
              mode={mode}
              setMode={setEditorMode}
              exitRedirectUrl={exitRedirectUrl}
              showButtons={!showCourseCreated}
              publishButtonTitle={publishButtonTitle}
              publishRedirectUrl={publishRedirectUrl}
              showMenu={showMenu}
              translationsOnly={translationsOnly}
              editingMode={editingMode}
              translationsLockedUntilPublish={translationsLockedUntilPublish}
              closeIconOnRight={closeIconOnRight}
            />
          )}
          {!sequence?.id && loadError && (
            <LoadError role="alert">{loadError}</LoadError>
          )}
          {!showCourseCreated && !translationsOnly && <GenerationFeedback />}
        </Content>
      )}
    </Wrapper>
  );
};
